import { ArmyData } from "../lib/db/types.js";
import { Keyword } from "./keyword.js";
import { Ruleset } from "./ruleset.js";
import { Unit } from "./unit.js";

export class Army {
  readonly ruleset: Ruleset;
  readonly data: ArmyData;

  constructor(ruleset: Ruleset, armyData: ArmyData) {
    this.ruleset = ruleset;
    this.data = armyData;
  }

  static ARMY_NAME_FALLBACK = "Unnamed Army";

  get name() {
    return this.data.name || Army.ARMY_NAME_FALLBACK;
  }

  get cost() {
    return this.units.reduce(
      (acc, unit) => acc + unit.cost * unit.data.count,
      this.keyword?.cost ?? 0,
    );
  }

  get units() {
    return this.data.units
      .map((unitData) => Unit.from(this, unitData))
      .filter((u) => !!u)
      .sort((left, right) => {
        return (
          (this.ruleset.unitOrderByProfileId.get(left.profile.id) ?? 0) -
          (this.ruleset.unitOrderByProfileId.get(right.profile.id) ?? 0)
        );
      })
      .sort((left, right) => {
        return (right.isLeader ? 1 : 0) - (left.isLeader ? 1 : 0);
      });
  }

  get unitsCount() {
    return this.units.reduce((acc, u) => acc + u.data.count, 0);
  }

  get keyword() {
    // Only a single keyword is allowed for an army
    const ref = this.data.keywords.at(0);
    return ref ? Keyword.from(this, ref) : null;
  }

  get summary() {
    return `${this.cost}pts, ${this.unitsCount} units`;
  }

  get failingConditions() {
    const ruleset = this.ruleset;
    const currentArmySize = this.unitsCount;
    const currentArmyCost = this.cost;
    const invalidUnitsCount = this.units.reduce(
      (acc, unit) => acc + (!unit.isValid ? 1 : 0),
      0,
    );

    return [
      currentArmyCost > this.ruleset.maxArmyCost
        ? {
            type: "ARMY_COST_ABOVE_MAX" as const,
            maxArmyCost: ruleset.maxArmyCost,
            currentArmyCost,
          }
        : null,

      currentArmySize < this.ruleset.minArmySize
        ? {
            type: "ARMY_SIZE_BELOW_MIN" as const,
            minArmySize: ruleset.minArmySize,
            currentArmySize,
          }
        : null,

      currentArmySize > this.ruleset.maxArmySize
        ? {
            type: "ARMY_SIZE_ABOVE_MAX" as const,
            maxArmySize: ruleset.maxArmySize,
            currentArmySize,
          }
        : null,

      invalidUnitsCount > 0
        ? {
            type: "INVALID_UNITS" as const,
            invalidUnitsCount,
          }
        : null,
    ].filter((condition) => !!condition);
  }

  getUnit(id: string) {
    return this.units.find((u) => u.data.id === id);
  }

  async setShareId(shareId: string) {
    // We intentionally don't bump ruleset version as we want it to match with
    // whatever was just shared.
    return await this.ruleset.db.updateArmy(
      { ...this.data, share_id: shareId },
      { allowSetShareId: true },
    );
  }
}
